'use client';

import { useMemo } from "react";
import { useLocale } from "@/lib/locale-context";
import { getMessage } from "@/lib/i18n-messages";
import { normalizeBrandText } from "@/config/brands";

/**
 * Read the active locale from the URL-derived LocaleProvider context.
 *
 * Why this no longer reads localStorage:
 *   The site now serves a separate URL per locale (/es/, /fr/, ...) and
 *   English at the root. The LanguageSwitcher navigates between those URLs;
 *   it does NOT swap the language in place. Reading from localStorage caused
 *   a real-time re-translation flash before the navigation completed and
 *   produced indexable English HTML for non-English URLs.
 *
 *   Pages outside app/[locale]/ (i.e. routes under app/(default)/) are
 *   English-only by design and the context defaults to 'en'.
 *
 * localStorage is still written by the LanguageSwitcher (via setStoredLocale)
 * so LocalePromptCard can offer the right suggestion on the user's next
 * visit, but it is no longer used to render the page.
 */
export const useTranslations = () => {
  const locale = useLocale();

  const t = useMemo(
    () => (key: string, fallback?: string) => normalizeBrandText(getMessage(locale, key, fallback)) ?? "",
    [locale]
  );

  return { locale, t };
};
